import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { SimplebarAngularModule } from 'simplebar-angular';
import { NotificationService } from '../../../core/services/notification.service';
import { NotificationItem } from '../../../core/models/notification.model';
import { TimeAgoPipe } from '../../pipes/time-ago.pipe';

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: [],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    BsDropdownModule,
    SimplebarAngularModule,
    RouterModule,
    TimeAgoPipe,
  ],
})
export class NotificationComponent implements OnInit, OnDestroy {
  items = signal<NotificationItem[]>([]);
  unreadCount = signal<number>(0);
  loading = signal<boolean>(false);
  canMarkAll = signal<boolean>(false);

  private pollInterval: any = null;

  constructor(
    private notificationService: NotificationService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadPermissions();
    this.loadNotifications();
    this.loadUnreadCount();
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  get badgeDisplay(): string {
    const count = this.unreadCount();
    if (count === 0) return '';
    return count > 9 ? '9+' : `${count}`;
  }

  onDropdownOpen(): void {
    this.loadNotifications();
  }

  async loadNotifications(): Promise<void> {
    this.loading.set(true);
    try {
      const response = await this.notificationService.list({ per_page: 5, status: 'unread' });
      const data = response?.data || response || [];
      const notifications: NotificationItem[] = (Array.isArray(data) ? data : []).map((item: any) => ({
        id: item.id,
        category: item.category || null,
        event: item.event || null,
        title: item.title || null,
        body: item.body || null,
        icon: item.icon || null,
        action_url: item.action_url || null,
        subject_type: item.subject_type || null,
        subject_id: item.subject_id || null,
        created_at: item.created_at || null,
        time_ago: item.time_ago || null,
        is_read: item.is_read || false,
      }));
      this.items.set(notifications);
    } catch (error) {
      console.error('Failed to load notifications', error);
    } finally {
      this.loading.set(false);
    }
  }

  async loadUnreadCount(): Promise<void> {
    try {
      const response = await this.notificationService.unreadCount();
      const count = response?.count ?? response?.data?.count ?? 0;
      this.unreadCount.set(count);
    } catch (error) {
      // Retain last known value on failure
      console.error('Failed to load unread count', error);
    }
  }

  async loadPermissions(): Promise<void> {
    try {
      const response = await this.notificationService.permissions();
      const allowMarkAll = response?.allow_mark_all_as_read ?? response?.data?.allow_mark_all_as_read ?? false;
      this.canMarkAll.set(allowMarkAll);
    } catch (error) {
      console.error('Failed to load notification permissions', error);
    }
  }

  async onItemSelect(item: NotificationItem): Promise<void> {
    if (!item.is_read) {
      try {
        await this.notificationService.markRead(item.id);
        // Immediately decrement badge
        this.unreadCount.update((count) => Math.max(0, count - 1));
        // Mark item as read locally
        this.items.update((items) =>
          items.map((i) => (i.id === item.id ? { ...i, is_read: true } : i))
        );
      } catch (error) {
        console.error('Failed to mark notification as read', error);
      }
    }
    if (item.action_url) {
      this.router.navigateByUrl(item.action_url);
    }
  }

  async markAllAsRead(): Promise<void> {
    try {
      await this.notificationService.markAllRead();
      this.items.update((items) => items.map((i) => ({ ...i, is_read: true })));
      this.unreadCount.set(0);
    } catch (error) {
      console.error('Failed to mark all as read', error);
    }
  }

  private startPolling(): void {
    this.pollInterval = setInterval(() => {
      this.loadUnreadCount();
    }, 60000);
  }

  private stopPolling(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }
}
